import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import {
  ChatBubbleOutline as ChatIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { exportProblemStatementToPdf } from '../../../utils/pdfExport';
import Markdown from 'react-markdown';

// Types for problem statement draft
export interface ProblemStatementDraft {
  title: string;
  problem: string;
  target_users: string;
  success_criteria: string;
  current_state: string;
  scale_and_growth: string;
  business_value: string;
  input_source: string;
  integrations: string[];
  constraints: string[];
  // Business context
  data_classification: string;
  process_criticality: string;
  human_judgment_level: number;
  risk_tolerance: string;
  communication_style: string;
}

export interface SectionFeedback {
  section: string;
  score: number;
  suggestion: string;
  is_weak: boolean;
}

interface ProblemStatementEditorProps {
  draft: ProblemStatementDraft | null;
  onSave?: (draft: ProblemStatementDraft) => Promise<void>;
  onAskAboutSection?: (section: string, prompt: string) => void;
  sectionFeedback: SectionFeedback[];
  qualityScore?: number;
  qualityScoreLabel?: string; // Display label (e.g., "Design Readiness")
  isLoading?: boolean;
  isSaving?: boolean;
}

// Section configuration with labels and prompts
const SECTIONS = [
  {
    key: 'title',
    label: 'Project Title',
    prompt: 'Help me create a clear, descriptive title for this agent project',
    placeholder: '[To be refined] - A short title for this agent (3-8 words)',
  },
  {
    key: 'problem',
    label: 'Core Problem',
    prompt: 'Help me better articulate the core problem this agent will solve',
    placeholder: '[To be refined] - What problem does this agent solve?',
  },
  {
    key: 'target_users',
    label: 'Target Users',
    prompt: 'Help me identify who will use this agent and their needs',
    placeholder: '[To be refined] - Who will use this agent?',
  },
  {
    key: 'success_criteria',
    label: 'Success Criteria',
    prompt: 'Help me define measurable success criteria for this agent',
    placeholder: '[To be refined] - How will we measure success?',
  },
  {
    key: 'current_state',
    label: 'Current State',
    prompt: 'Help me describe what happens today without this agent',
    placeholder: '[To be refined] - What happens without this agent?',
  },
  {
    key: 'scale_and_growth',
    label: 'Scale & Growth',
    prompt: 'Help me understand the volume and growth expectations',
    placeholder: '[To be refined] - Volume, frequency, and growth expectations',
  },
  {
    key: 'business_value',
    label: 'Business Value',
    prompt: 'Help me articulate the business value this agent will deliver',
    placeholder:
      '[To be refined] - Cost reduction, revenue, time savings, etc.',
  },
  {
    key: 'input_source',
    label: 'Input Source',
    prompt: 'Help me identify how work arrives for this agent',
    placeholder: '[To be refined] - Manual, system event, scheduled, etc.',
  },
  {
    key: 'integrations',
    label: 'Required Integrations',
    prompt: 'Help me identify what systems this agent needs to connect to',
    placeholder: '[To be refined]',
  },
  {
    key: 'constraints',
    label: 'Constraints',
    prompt: 'Help me identify any constraints or limitations to consider',
    placeholder: '[To be refined]',
  },
  {
    key: 'data_classification',
    label: 'Data Classification',
    prompt: 'Help me determine the sensitivity level of data involved',
    placeholder:
      '[To be refined] - Public, internal, confidential, or regulated',
  },
  {
    key: 'process_criticality',
    label: 'Process Criticality',
    prompt: 'Help me assess how critical this process is to the business',
    placeholder: '[To be refined] - Low, medium, or high',
  },
];

// Convert draft to markdown
const draftToMarkdown = (draft: ProblemStatementDraft): string => {
  const formatList = (items: unknown): string => {
    const arr = toStringArray(items);
    if (arr.length === 0) return '- [To be refined]';
    return arr.map((item) => `- ${item}`).join('\n');
  };

  const title = draft.title || 'Problem Statement';

  return `# ${title}

## Core Problem
${draft.problem || '[To be refined]'}

## Target Users
${draft.target_users || '[To be refined]'}

## Success Criteria
${draft.success_criteria || '[To be refined]'}

## Current State
${draft.current_state || '[To be refined]'}

## Scale & Growth
${draft.scale_and_growth || '[To be refined]'}

## Business Value
${draft.business_value || '[To be refined]'}

## Input Source
${draft.input_source || '[To be refined]'}

## Required Integrations
${formatList(draft.integrations)}

## Constraints
${formatList(draft.constraints)}

## Business Context
- **Data Classification**: ${draft.data_classification || '[To be refined]'}
- **Process Criticality**: ${draft.process_criticality || '[To be refined]'}
- **Human Judgment Level**: ${draft.human_judgment_level || 3}/5
- **Risk Tolerance**: ${draft.risk_tolerance || '[To be refined]'}
`;
};

// Get feedback for a section
const getFeedbackForSection = (
  sectionKey: string,
  feedback: SectionFeedback[]
): SectionFeedback | undefined => {
  return feedback.find((f) => f.section === sectionKey);
};

// Section header with status indicator
const SectionHeader: React.FC<{
  label: string;
  feedback?: SectionFeedback;
  onAskClick?: () => void;
}> = ({ label, feedback, onAskClick }) => {
  const isWeak = feedback?.is_weak ?? false;
  const score = feedback?.score ?? 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">
          {label}
        </Typography>
        {score >= 0.7 ? (
          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
        )}
      </Box>
      {onAskClick && isWeak && (
        <Tooltip title="Ask agent about this section">
          <IconButton size="small" onClick={onAskClick} sx={{ p: 0.5 }}>
            <ChatIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// Inline hint for weak sections
const InlineHint: React.FC<{ feedback?: SectionFeedback }> = ({ feedback }) => {
  if (!feedback?.is_weak || !feedback?.suggestion) return null;

  return (
    <Typography
      variant="caption"
      sx={{
        color: 'warning.dark',
        display: 'block',
        mt: 0.5,
        fontStyle: 'italic',
      }}
    >
      {feedback.suggestion}
    </Typography>
  );
};

// Helper to safely convert a value to an array of strings
const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string');
  }
  if (typeof value === 'string' && value.trim()) {
    // If it's a string, split by newlines or commas
    return value
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

// Helper to compare drafts for equality
const draftsAreEqual = (
  a: ProblemStatementDraft | null,
  b: ProblemStatementDraft | null
): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return (
    a.title === b.title &&
    a.problem === b.problem &&
    a.target_users === b.target_users &&
    a.success_criteria === b.success_criteria &&
    a.current_state === b.current_state &&
    a.scale_and_growth === b.scale_and_growth &&
    a.business_value === b.business_value &&
    a.input_source === b.input_source &&
    a.data_classification === b.data_classification &&
    a.process_criticality === b.process_criticality &&
    a.human_judgment_level === b.human_judgment_level &&
    a.risk_tolerance === b.risk_tolerance &&
    JSON.stringify(a.integrations) === JSON.stringify(b.integrations) &&
    JSON.stringify(a.constraints) === JSON.stringify(b.constraints)
  );
};

const ProblemStatementEditor: React.FC<ProblemStatementEditorProps> = ({
  draft,
  onSave,
  onAskAboutSection,
  sectionFeedback,
  qualityScore,
  qualityScoreLabel = 'Design Readiness', // Default to "Design Readiness" for Discovery
  isLoading = false,
  isSaving = false,
}) => {
  const [tab, setTab] = useState(0);
  // Track local edits separately from the saved draft
  const [localDraft, setLocalDraft] = useState<ProblemStatementDraft | null>(
    draft
  );
  const [savedDraft, setSavedDraft] = useState<ProblemStatementDraft | null>(
    draft
  );
  // Track raw text for list fields to allow free-form editing
  const [integrationsText, setIntegrationsText] = useState(
    toStringArray(draft?.integrations).join('\n')
  );
  const [constraintsText, setConstraintsText] = useState(
    toStringArray(draft?.constraints).join('\n')
  );
  // Track the last draft we received from parent to detect external changes
  const lastExternalDraft = useRef<ProblemStatementDraft | null>(draft);

  // Sync local draft only when parent sends a genuinely new draft (from agent responses)
  useEffect(() => {
    // Only sync if the draft from parent is different from what we last received
    // This allows agent responses to update the draft while preserving user edits
    if (draft !== lastExternalDraft.current) {
      lastExternalDraft.current = draft;
      setLocalDraft(draft);
      setSavedDraft(draft);
      // Also sync raw text for list fields
      setIntegrationsText(toStringArray(draft?.integrations).join('\n'));
      setConstraintsText(toStringArray(draft?.constraints).join('\n'));
    }
  }, [draft]);

  // Check if there are unsaved changes (including raw text for list fields)
  const hasUnsavedChanges =
    !draftsAreEqual(localDraft, savedDraft) ||
    integrationsText !== toStringArray(savedDraft?.integrations).join('\n') ||
    constraintsText !== toStringArray(savedDraft?.constraints).join('\n');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Handle field changes (local only, no API call)
  const handleFieldChange = useCallback(
    (field: keyof ProblemStatementDraft, value: string | string[]) => {
      setLocalDraft((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [field]: value,
        };
      });
    },
    []
  );

  // Handle list field changes (integrations, constraints)
  // Store raw text to allow free-form editing including deletion
  const handleListFieldChange = useCallback(
    (field: 'integrations' | 'constraints', text: string) => {
      if (field === 'integrations') {
        setIntegrationsText(text);
      } else {
        setConstraintsText(text);
      }
    },
    []
  );

  // Parse raw text into array items (used when saving)
  const parseListText = (text: string): string[] => {
    return text
      .split('\n')
      .map((s) => s.replace(/^-\s*/, '').trim())
      .filter(Boolean);
  };

  // Handle save button click
  const handleSave = useCallback(async () => {
    if (!localDraft || !onSave) return;
    // Parse raw text into arrays for list fields
    const draftToSave = {
      ...localDraft,
      integrations: parseListText(integrationsText),
      constraints: parseListText(constraintsText),
    };
    await onSave(draftToSave);
    setSavedDraft(draftToSave); // Mark as saved after successful save
    setLocalDraft(draftToSave); // Update local draft with cleaned values
    // Update raw text to cleaned version (without empty lines)
    setIntegrationsText(draftToSave.integrations.join('\n'));
    setConstraintsText(draftToSave.constraints.join('\n'));
  }, [localDraft, onSave, integrationsText, constraintsText]);

  // Empty state
  if (!localDraft) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Continue chatting to generate your first draft problem statement.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          The draft will appear here after 2-3 exchanges.
        </Typography>
      </Paper>
    );
  }

  const markdown = draftToMarkdown(localDraft);

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Problem Statement Draft
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
            <Tooltip title="Export as PDF">
              <IconButton
                size="small"
                onClick={() => exportProblemStatementToPdf(localDraft!)}
                disabled={!localDraft}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="problem statement editor tabs"
        >
          <Tab label="Preview" />
          <Tab label="Edit" />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tab === 0 ? (
          // Preview tab - rendered markdown
          <Box
            className="markdown-preview"
            sx={{
              '& h1': { fontSize: '1.5rem' },
              '& h2': { fontSize: '1.1rem', mt: 2 },
            }}
          >
            <Markdown>{markdown}</Markdown>
          </Box>
        ) : (
          // Edit tab - form fields with inline hints
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SECTIONS.map((section) => {
              const feedback = getFeedbackForSection(
                section.key,
                sectionFeedback
              );
              const isListField =
                section.key === 'integrations' || section.key === 'constraints';
              const value =
                localDraft[section.key as keyof ProblemStatementDraft];

              return (
                <Box key={section.key}>
                  <SectionHeader
                    label={section.label}
                    feedback={feedback}
                    onAskClick={
                      onAskAboutSection && feedback?.is_weak
                        ? () => onAskAboutSection(section.key, section.prompt)
                        : undefined
                    }
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={isListField ? 2 : 3}
                    size="small"
                    placeholder={section.placeholder}
                    value={
                      isListField
                        ? section.key === 'integrations'
                          ? integrationsText
                          : constraintsText
                        : (value as string) || ''
                    }
                    onChange={(e) => {
                      if (isListField) {
                        handleListFieldChange(
                          section.key as 'integrations' | 'constraints',
                          e.target.value
                        );
                      } else {
                        handleFieldChange(
                          section.key as keyof ProblemStatementDraft,
                          e.target.value
                        );
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: feedback?.is_weak
                          ? 'warning.50'
                          : 'background.paper',
                      },
                    }}
                  />
                  <InlineHint feedback={feedback} />
                </Box>
              );
            })}

            {/* Save button */}
            {onSave && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                >
                  {isSaving
                    ? 'Saving...'
                    : hasUnsavedChanges
                      ? 'Save Changes'
                      : 'Saved'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ProblemStatementEditor;
